import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { http } from "../../lib/http";
import { useAuth } from "../../contexts/AuthContext";
import { PageHeader, PageBody } from "../../components/shell/PageChrome";
import ExportMenu from "../../components/ExportMenu";
import { DataTable, PageToolbar, PrimaryAddButton } from "../../components/data/DataTable";
import TablePagination from "../../components/data/TablePagination";
import ViewDrawer from "../../components/data/ViewDrawer";
import ConfirmDialog from "../../components/data/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

const EMPTY = {
  family_name: "",
  primary_parent: { first_name: "", last_name: "", nif_nie: "", phone: "", email: "", relationship: "mother" },
  address: { line1: "", city: "", postcode: "", province: "", country: "ES" },
  billing: { billing_name: "", billing_nif: "", billing_email: "", payment_method: "transfer", iban: "", bic: "" },
  gdpr: { data_processing_consent: true, marketing_consent: false, image_rights_consent: false, consent_date: new Date().toISOString().slice(0, 10) },
  notes: "",
  active: true,
};

export default function FamiliesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "receptionist"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (activeFilter !== "all") params.set("active", activeFilter === "active");
      if (paymentFilter !== "all") params.set("payment_method", paymentFilter);
      params.set("skip", (page - 1) * PAGE_SIZE);
      params.set("limit", PAGE_SIZE);
      const r = await http.get(`/families?${params}`);
      setItems(r.data.items);
      setTotal(r.data.total ?? (r.data.items || []).length);
    } finally { setLoading(false); }
  }, [q, activeFilter, paymentFilter, page]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [q, activeFilter, paymentFilter]);

  const openView = async (row) => {
    const r = await http.get(`/families/${row.id}`);
    setView(r.data);
  };

  const save = async (data, id) => {
    try {
      if (id) await http.patch(`/families/${id}`, data);
      else await http.post(`/families`, data);
      toast.success(t("families.saved"));
      setEdit(null);
      fetchList();
    } catch (e) {
      const errs = e.response?.data?.detail?.errors || [e.response?.data?.detail || "Save failed"];
      toast.error(Array.isArray(errs) ? errs.join("; ") : String(errs));
    }
  };

  const doDelete = async () => {
    try {
      await http.delete(`/families/${deleting.id}`);
      toast.success(t("families.deleted"));
      setDeleting(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      setDeleting(null);
    }
  };

  const columns = [
    { key: "family_name", label: t("families.name"), render: (r) => <span className="font-medium text-[#0A1F44]">{r.family_name}</span> },
    { key: "parent", label: t("families.primaryParent"), render: (r) => `${r.primary_parent?.first_name || ""} ${r.primary_parent?.last_name || ""}` },
    { key: "phone", label: t("families.phone"), render: (r) => r.primary_parent?.phone || "—" },
    { key: "email", label: t("families.email"), render: (r) => r.primary_parent?.email || "—" },
    { key: "students", label: t("families.students"), render: (r) => <Badge variant="outline">{r.student_count}</Badge> },
    { key: "payment", label: t("families.payment"), render: (r) => <Badge variant="secondary">{r.billing?.payment_method || "—"}</Badge> },
    { key: "active", label: t("families.status"), render: (r) => <Badge className={r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}>{r.active ? t("common.active") : t("common.inactive")}</Badge> },
    {
      key: "actions", label: t("common.actions"), tdClass: "text-right", thClass: "text-right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => openView(r)} data-testid={`families-view-${r.id}`}><Eye size={14} /></Button>
          {canEdit && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`families-edit-${r.id}`}><Pencil size={14} /></Button>}
          {isAdmin && <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} data-testid={`families-delete-${r.id}`}><Trash2 size={14} className="text-[#C8102E]" /></Button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="families-page">
      <PageHeader title={t("families.title")} subtitle={t("families.subtitle")} />
      <PageBody>
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4" data-testid="families-actions-row">
          {canEdit && <PrimaryAddButton label={t("families.addFamily")} onClick={() => setEdit({ ...EMPTY })} testid="families-add-button" />}
          <ExportMenu resource="families" />
        </div>
        <PageToolbar
          testid="families-toolbar"
          search={q} onSearch={setQ}
          filters={(
            <>
              <Select value={activeFilter} onValueChange={setActiveFilter}>
                <SelectTrigger className="w-[140px] bg-white" data-testid="families-filter-active"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  <SelectItem value="active">{t("common.active")}</SelectItem>
                  <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-[140px] bg-white" data-testid="families-filter-payment"><SelectValue placeholder={t("families.payment")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  <SelectItem value="sepa">SEPA</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          rightExtra={<TablePagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} testid="families-pagination-top" />}
        />
        {loading ? <div className="text-slate-500">{t("common.loading")}</div> : (
          <>
            <DataTable
              testid="families-table"
              columns={columns}
              rows={items}
              onRowDoubleClick={(r) => canEdit ? setEdit(r) : openView(r)}
              emptyText={t("families.empty")}
            />
            <div className="mt-4 flex justify-end">
              <TablePagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} testid="families-pagination-bottom" />
            </div>
          </>
        )}
      </PageBody>

      <ViewDrawer open={!!view} onClose={() => setView(null)} title={view?.family_name || ""} testid="family-drawer">
        {view && <FamilyDetail family={view} />}
      </ViewDrawer>

      <FamilyEditModal
        open={!!edit}
        initial={edit}
        onCancel={() => setEdit(null)}
        onSave={(d) => save(d, edit?.id)}
      />

      <ConfirmDialog
        open={!!deleting}
        title={t("families.deleteConfirmTitle")}
        message={t("families.deleteConfirmBody", { name: deleting?.family_name })}
        confirmText={t("common.delete")}
        danger
        onCancel={() => setDeleting(null)}
        onConfirm={doDelete}
        testid="family-delete-confirm"
      />
    </div>
  );
}

function FamilyDetail({ family }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6 text-sm" data-testid="family-detail">
      <Section title={t("families.section.parent")}>
        <Row k={t("families.name")} v={`${family.primary_parent?.first_name} ${family.primary_parent?.last_name}`} />
        <Row k="NIF/NIE" v={family.primary_parent?.nif_nie} />
        <Row k={t("families.phone")} v={family.primary_parent?.phone} />
        <Row k={t("families.email")} v={family.primary_parent?.email} />
      </Section>
      <Section title={t("families.section.address")}>
        <Row k="" v={`${family.address?.line1 || ""}, ${family.address?.city || ""} ${family.address?.postcode || ""}, ${family.address?.province || ""}`} />
      </Section>
      <Section title={t("families.section.billing")}>
        <Row k={t("families.payment")} v={family.billing?.payment_method} />
        {family.billing?.iban && <Row k="IBAN" v={family.billing?.iban} />}
        {family.billing?.bic && <Row k="BIC" v={family.billing?.bic} />}
      </Section>
      <Section title={t("families.section.gdpr")}>
        <Row k={t("families.gdpr.processing")} v={family.gdpr?.data_processing_consent ? "✓" : "—"} />
        <Row k={t("families.gdpr.marketing")} v={family.gdpr?.marketing_consent ? "✓" : "—"} />
        <Row k={t("families.gdpr.image")} v={family.gdpr?.image_rights_consent ? "✓" : "—"} />
      </Section>
      <Section title={t("families.section.students")}>
        {(family.students || []).length === 0 ? <p className="text-slate-500">—</p> : (
          <ul className="space-y-1.5">
            {family.students.map((s) => (
              <li key={s.id} className="flex justify-between border-b border-slate-100 pb-1.5">
                <span>{s.first_name} {s.last_name}</span>
                <Badge variant="outline">{s.level}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{k}</span>
      <span className="text-[#0A1F44] text-right">{v || "—"}</span>
    </div>
  );
}

function FamilyEditModal({ open, initial, onCancel, onSave }) {
  const { t } = useTranslation();
  const [d, setD] = useState(initial || EMPTY);
  useEffect(() => { setD(initial || EMPTY); }, [initial]);
  if (!open) return null;
  const u = (path, val) => {
    const next = JSON.parse(JSON.stringify(d));
    const keys = path.split(".");
    let cur = next;
    while (keys.length > 1) cur = cur[keys.shift()];
    cur[keys[0]] = val;
    setD(next);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="family-edit-modal">
        <DialogHeader><DialogTitle>{initial?.id ? t("families.edit") : t("families.createTitle")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <Field label={t("families.name")} required>
            <Input value={d.family_name} onChange={(e) => u("family_name", e.target.value)} data-testid="family-name-input" />
          </Field>
          <Field label={t("families.primaryParent")} required>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First" value={d.primary_parent?.first_name || ""} onChange={(e) => u("primary_parent.first_name", e.target.value)} data-testid="family-pp-first" />
              <Input placeholder="Last" value={d.primary_parent?.last_name || ""} onChange={(e) => u("primary_parent.last_name", e.target.value)} data-testid="family-pp-last" />
            </div>
          </Field>
          <Field label="NIF/NIE">
            <Input value={d.primary_parent?.nif_nie || ""} onChange={(e) => u("primary_parent.nif_nie", e.target.value.toUpperCase())} data-testid="family-pp-nif" />
          </Field>
          <Field label={t("families.phone")}>
            <Input value={d.primary_parent?.phone || ""} onChange={(e) => u("primary_parent.phone", e.target.value)} />
          </Field>
          <Field label={t("families.email")}>
            <Input type="email" value={d.primary_parent?.email || ""} onChange={(e) => u("primary_parent.email", e.target.value)} />
          </Field>
          <Field label={t("families.payment")}>
            <Select value={d.billing?.payment_method || "transfer"} onValueChange={(v) => u("billing.payment_method", v)}>
              <SelectTrigger data-testid="family-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sepa">SEPA</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {d.billing?.payment_method === "sepa" && (
            <>
              <Field label="IBAN">
                <Input value={d.billing?.iban || ""} onChange={(e) => u("billing.iban", e.target.value.toUpperCase())} data-testid="family-iban" />
              </Field>
              <Field label="BIC">
                <Input value={d.billing?.bic || ""} onChange={(e) => u("billing.bic", e.target.value.toUpperCase())} data-testid="family-bic" />
              </Field>
            </>
          )}
          <Field label="GDPR" className="md:col-span-2">
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2"><Checkbox checked={d.gdpr?.data_processing_consent} onCheckedChange={(v) => u("gdpr.data_processing_consent", !!v)} data-testid="family-gdpr-processing" /> {t("families.gdpr.processing")}</label>
              <label className="flex items-center gap-2"><Checkbox checked={d.gdpr?.image_rights_consent} onCheckedChange={(v) => u("gdpr.image_rights_consent", !!v)} /> {t("families.gdpr.image")}</label>
              <label className="flex items-center gap-2"><Checkbox checked={d.gdpr?.marketing_consent} onCheckedChange={(v) => u("gdpr.marketing_consent", !!v)} /> {t("families.gdpr.marketing")}</label>
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="family-cancel-btn">{t("common.cancel")}</Button>
          <Button onClick={() => onSave(d)} className="bg-[#0A1F44] text-white hover:bg-[#091a3a]" data-testid="family-save-btn">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}{required ? <span className="text-[#C8102E]"> *</span> : null}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
